import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, CssBaseline, Grid, Typography, Backdrop, CircularProgress } from "@mui/material";

import { mainLayoutStyles } from "../../../../constants";
import Header from "../../../../common/Header";
import Sidebar from "../../../../common/Sidebar";
import { useAuth } from "../../../../hooks";

import DateRangeSelector from "../../components/DateRangeSelector/DateRangeSelector";
import VisitsStackedBarChart from "../../components/VisitsStackedBarChart/VisitsStackedBarChart";
import PlaysPerQuickTaleChart from "../../components/PlaysPerQuickTaleChart/PlaysPerQuickTaleChart";
import PlaysPerDayChart from "../../components/PlaysPerDayChart/PlaysPerDayChart";

import {
  fetchOrganizationVisits,
  fetchContentCardClicks,
  fetchQuickTaleTitles,
  normalizeRange,
  formatRangeLabel,
} from "../../hooks/OrganizationDashboardHooks";

const OrganizationDashboardContainer = () => {
  const { currentUser, organizationId } = useAuth();
  const orgId = organizationId || currentUser?.organization || null;

  // Date range selector state (defaults to current month-to-date)
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return [new Date(now.getFullYear(), now.getMonth(), 1), now];
  });
  const [startDate, endDate] = dateRange;

  // Data states
  const [visitsData, setVisitsData] = useState({ totalVisits: 0, uniqueVisitors: 0, visitsByDay: {}, uniqueVisitorsByDay: {} });
  const [perQuickTaleData, setPerQuickTaleData] = useState([]);
  const [clicksByDay, setClicksByDay] = useState({});
  const [clicksByDayPerQuickTale, setClicksByDayPerQuickTale] = useState({});

  // Currently selected quicktale (for filtering the Plays per Day chart)
  const [selectedQuickTaleId, setSelectedQuickTaleId] = useState(null);

  // Loading states
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [clicksLoading, setClicksLoading] = useState(false);

  // Human-readable label for the selected range (for display)
  const selectedRangeLabel = useMemo(
    () => formatRangeLabel(startDate, endDate),
    [startDate, endDate]
  );

  // Fetch data when org or date range changes
  const loadData = useCallback(async () => {
    console.log("=== OrganizationDashboard loadData ===");
    console.log("orgId:", orgId);
    console.log("organizationId from useAuth:", organizationId);
    console.log("currentUser?.organization:", currentUser?.organization);

    if (!orgId) {
      console.log("No orgId available, skipping data load");
      return;
    }

    const range = normalizeRange(startDate, endDate);
    if (!range) {
      console.log("Incomplete date range, skipping data load");
      return;
    }

    const { startDate: rangeStart, endDate: rangeEnd } = range;
    console.log("Date range:", rangeStart.toISOString(), "to", rangeEnd.toISOString());

    // Fetch visits
    setVisitsLoading(true);
    try {
      const visits = await fetchOrganizationVisits(orgId, rangeStart, rangeEnd);
      setVisitsData(visits);
    } catch (error) {
      console.error("Error loading visits:", error);
    } finally {
      setVisitsLoading(false);
    }

    // Fetch clicks
    setClicksLoading(true);
    try {
      const {
        perQuickTale,
        clicksByDay: dayClicks,
        clicksByDayPerQuickTale: dayClicksPerQT,
      } = await fetchContentCardClicks(orgId, rangeStart, rangeEnd);

      // Fetch QuickTale titles for the objectIds
      const objectIds = perQuickTale.map((item) => item.objectId);
      const titleMap = await fetchQuickTaleTitles(objectIds);

      // Merge titles into the data
      const enrichedData = perQuickTale.map((item) => ({
        ...item,
        title: titleMap[item.objectId] || item.contentName || "Unknown",
      }));

      setPerQuickTaleData(enrichedData);
      setClicksByDay(dayClicks);
      setClicksByDayPerQuickTale(dayClicksPerQT || {});
      // Reset any active quicktale filter when new data is loaded
      setSelectedQuickTaleId(null);
    } catch (error) {
      console.error("Error loading clicks:", error);
    } finally {
      setClicksLoading(false);
    }
  }, [orgId, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers for selecting/clearing a quicktale to filter the Plays per Day chart
  const handleSelectQuickTale = useCallback((objectId) => {
    setSelectedQuickTaleId(objectId);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedQuickTaleId(null);
  }, []);

  // Title of the selected quicktale (for display in the Plays per Day chart)
  const selectedQuickTaleTitle = useMemo(() => {
    if (!selectedQuickTaleId) return null;
    const found = perQuickTaleData.find((item) => item.objectId === selectedQuickTaleId);
    return found ? found.title : null;
  }, [selectedQuickTaleId, perQuickTaleData]);

  // The day-level data to display: filtered to the selected quicktale, or the overall totals
  const displayedClicksByDay = useMemo(() => {
    if (selectedQuickTaleId && clicksByDayPerQuickTale[selectedQuickTaleId]) {
      return clicksByDayPerQuickTale[selectedQuickTaleId];
    }
    return clicksByDay;
  }, [selectedQuickTaleId, clicksByDayPerQuickTale, clicksByDay]);

  return (
    <Box sx={{ display: "flex", background: "#F5F5F5" }}>
      <CssBaseline />
      <Header mainTitle={"Metrics Dashboard"} />
      <Sidebar />
      <Grid
        container
        style={{
          ...mainLayoutStyles,
          height: undefined,
          minHeight: "96vh",
          backgroundColor: "#F5F5F5",
        }}
      >
        <Grid container direction={"column"} style={{ background: "#F5F5F5" }}>
          {/* Month Selector */}
          <Grid
            container
            style={{ padding: "20px 30px 10px 30px" }}
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <Typography
                sx={{
                  fontFamily: "Avenir Book, sans-serif",
                  fontSize: 20,
                  color: "#666",
                  fontWeight: 600,
                  marginTop: '20px'
                }}
              >
                Select date range:
              </Typography>
            </Grid>
            <Grid item>
              <DateRangeSelector
                startDate={startDate}
                endDate={endDate}
                onChange={setDateRange}
              />
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container style={{ padding: "10px 30px 30px 30px" }} spacing={3}>
            {/* Unique Visitors per Day - Bar Chart */}
            <Grid item xs={12} md={6}>
              <Grid
                container
                direction={"column"}
                style={{
                  background: "#fff",
                  width: "100%",
                  minHeight: 380,
                  borderRadius: 5,
                  padding: 20,
                }}
              >
                <VisitsStackedBarChart
                  metric="unique"
                  totalVisits={visitsData.totalVisits}
                  uniqueVisitors={visitsData.uniqueVisitors}
                  visitsByDay={visitsData.visitsByDay}
                  uniqueVisitorsByDay={visitsData.uniqueVisitorsByDay}
                  loading={visitsLoading}
                  rangeLabel={selectedRangeLabel}
                />
              </Grid>
            </Grid>

            {/* Total Plays per Day */}
            <Grid item xs={12} md={6}>
              <Grid
                container
                direction={"column"}
                style={{
                  background: "#fff",
                  width: "100%",
                  minHeight: 380,
                  borderRadius: 5,
                  padding: 20,
                }}
              >
                <PlaysPerDayChart
                  clicksByDay={displayedClicksByDay}
                  loading={clicksLoading}
                  selectedQuickTaleTitle={selectedQuickTaleTitle}
                  onClearSelection={handleClearSelection}
                  rangeLabel={selectedRangeLabel}
                />
              </Grid>
            </Grid>

            {/* Plays per QuickTale - Full Width */}
            <Grid item xs={12}>
              <Grid
                container
                direction={"column"}
                style={{
                  background: "#fff",
                  width: "100%",
                  minHeight: 450,
                  borderRadius: 5,
                  padding: 20,
                }}
              >
                <PlaysPerQuickTaleChart
                  perQuickTaleData={perQuickTaleData}
                  loading={clicksLoading}
                  selectedQuickTaleId={selectedQuickTaleId}
                  onSelectQuickTale={handleSelectQuickTale}
                  rangeLabel={selectedRangeLabel}
                />
              </Grid>
            </Grid>

            {/* Total Visits per Day - Full Width */}
            <Grid item xs={12}>
              <Grid
                container
                direction={"column"}
                style={{
                  background: "#fff",
                  width: "100%",
                  minHeight: 380,
                  borderRadius: 5,
                  padding: 20,
                }}
              >
                <VisitsStackedBarChart
                  metric="total"
                  totalVisits={visitsData.totalVisits}
                  uniqueVisitors={visitsData.uniqueVisitors}
                  visitsByDay={visitsData.visitsByDay}
                  uniqueVisitorsByDay={visitsData.uniqueVisitorsByDay}
                  loading={visitsLoading}
                  rangeLabel={selectedRangeLabel}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationDashboardContainer;
